import { useCallback, useEffect, useState } from "react";
import { API_BASE_URL } from "../utils/config";

const authHeaders = () => {
  const token = localStorage.getItem("accessToken");
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

const formatDateTime = (iso) =>
  iso ? new Date(iso).toLocaleString("en-SG", { dateStyle: "short", timeStyle: "short" }) : "-";

// A repair happens in one gudang (REPAIR moves that gudang's damaged back to
// its own available), so the modal needs the per-gudang breakdown, not the
// item-wide total.
const damagedLocations = (item) =>
  (item.location_stocks || []).filter((ls) => (ls.damaged ?? 0) > 0);

const errorMessage = (data, fallback) => {
  if (data && typeof data === "object") {
    const firstKey = Object.keys(data)[0];
    if (firstKey && Array.isArray(data[firstKey])) return data[firstKey][0];
    if (typeof data[firstKey] === "string") return data[firstKey];
    if (data.detail) return data.detail;
  }
  return fallback;
};

function DamagedItems() {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  const [page, setPage] = useState(1);
  const pageSize = 25;
  const [totalCount, setTotalCount] = useState(0);

  // bumped after a repair so the list and the history refetch
  const [reloadKey, setReloadKey] = useState(0);
  const [repairs, setRepairs] = useState([]);
  const [notice, setNotice] = useState("");

  // repair modal
  const [repairItem, setRepairItem] = useState(null);
  const [repairLocationId, setRepairLocationId] = useState("");
  const [repairQty, setRepairQty] = useState("");
  const [repairBy, setRepairBy] = useState("");
  const [repairNote, setRepairNote] = useState("");
  const [repairError, setRepairError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setPage(1);
  }, [search]);

  useEffect(() => {
    setLoading(true);

    const params = new URLSearchParams();
    params.set("page", page);
    params.set("page_size", pageSize);
    params.set("damaged_only", "1");

    if (search.trim()) {
      params.set("search", search.trim());
    }

    fetch(`${API_BASE_URL}/api/items/?${params.toString()}`, {
      headers: authHeaders(),
    })
      .then((response) => response.json())
      .then((data) => {
        const results = Array.isArray(data) ? data : data.results || [];
        setItems(results);

        if (!Array.isArray(data) && typeof data?.count === "number") {
          setTotalCount(data.count);
        } else {
          setTotalCount(results.length);
        }

        setLoading(false);
      })
      .catch((error) => {
        console.log("Error fetching damaged items:", error);
        setLoading(false);
      });
  }, [page, search, reloadKey]);

  const loadRepairs = useCallback(() => {
    fetch(`${API_BASE_URL}/api/transactions/?transaction_type=REPAIR&page_size=10`, {
      headers: authHeaders(),
    })
      .then((response) => response.json())
      .then((data) => setRepairs(Array.isArray(data) ? data : data.results || []))
      .catch((error) => console.log("Error fetching repair history:", error));
  }, []);

  useEffect(() => {
    loadRepairs();
  }, [loadRepairs, reloadKey]);

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  const maxRepairQty = (() => {
    if (!repairItem) return 0;
    const ls = damagedLocations(repairItem).find(
      (l) => String(l.location_id) === String(repairLocationId)
    );
    return ls ? ls.damaged : 0;
  })();

  const openRepair = (item) => {
    const locs = damagedLocations(item);
    setRepairItem(item);
    setRepairLocationId(locs.length === 1 ? String(locs[0].location_id) : "");
    setRepairQty("");
    setRepairBy("");
    setRepairNote("");
    setRepairError("");
  };

  const closeRepair = () => {
    if (!saving) setRepairItem(null);
  };

  const submitRepair = (e) => {
    e.preventDefault();
    setRepairError("");

    const qty = Number(repairQty);
    if (!repairLocationId) {
      setRepairError("Pilih gudang tempat barang rusak ini berada.");
      return;
    }
    if (!Number.isInteger(qty) || qty <= 0) {
      setRepairError("Jumlah yang diperbaiki harus lebih dari 0.");
      return;
    }
    if (qty > maxRepairQty) {
      setRepairError(`Stok rusak di gudang ini cuma ${maxRepairQty}.`);
      return;
    }
    if (!repairBy.trim()) {
      setRepairError("Isi siapa yang memperbaiki barang ini.");
      return;
    }

    setSaving(true);
    fetch(`${API_BASE_URL}/api/transactions/create/`, {
      method: "POST",
      headers: authHeaders(),
      body: JSON.stringify({
        item_id: repairItem.id,
        location_id: Number(repairLocationId),
        transaction_type: "REPAIR",
        quantity: qty,
        assign_name: repairBy.trim(),
        note: repairNote.trim(),
      }),
    })
      .then(async (res) => {
        const data = await res.json().catch(() => null);
        if (!res.ok) throw new Error(errorMessage(data, "Gagal menyimpan perbaikan."));
        return data;
      })
      .then((tx) => {
        setNotice(
          tx.status === "PENDING"
            ? `${qty} ${repairItem.name} diajukan sebagai diperbaiki. Menunggu persetujuan (acc) admin.`
            : `${qty} ${repairItem.name} selesai diperbaiki dan kembali jadi stok tersedia.`
        );
        setRepairItem(null);
        setReloadKey((k) => k + 1);
      })
      .catch((err) => setRepairError(err.message))
      .finally(() => setSaving(false));
  };

  const inputClass =
    "w-full rounded-md border border-white/50 dark:border-white/15 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 bg-white/80 dark:bg-white/5";

  return (
    <div>
      <h1 className="text-2xl font-semibold mb-4">Damaged Items</h1>

      {notice && (
        <div className="mb-4 flex items-start justify-between gap-3 rounded-md border border-green-500/40 bg-green-500/10 px-3 py-2 text-sm text-green-700 dark:text-green-300">
          <span>{notice}</span>
          <button type="button" onClick={() => setNotice("")} className="text-xs font-semibold">
            Tutup
          </button>
        </div>
      )}

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4">
        <div className="w-full sm:max-w-md">
          <label className="block text-xs font-medium text-muted mb-1">
            Search (code, name, category)
          </label>
          <input
            type="text"
            placeholder="e.g. cable, microphone, LGT-001..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={inputClass}
          />
        </div>
      </div>

      <div className="glass-table-wrap overflow-x-auto">
        <table className="glass-table">
          <thead>
            <tr>
              <th className="px-4 py-2 text-left">Code</th>
              <th className="px-4 py-2 text-left">Name</th>
              <th className="px-4 py-2 text-left">Category</th>
              <th className="px-4 py-2 text-left">Gudang (rusak)</th>
              <th className="px-4 py-2 text-center">Available</th>
              <th className="px-4 py-2 text-center">Damaged</th>
              <th className="px-4 py-2 text-right">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={7} className="px-4 py-4 text-center text-muted">
                  Loading damaged items...
                </td>
              </tr>
            )}

            {!loading && items.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-4 text-center text-muted">
                  No damaged items found.
                </td>
              </tr>
            )}

            {!loading &&
              items.map((item) => {
                const locs = damagedLocations(item);
                return (
                  <tr key={item.id} className="border-t">
                    <td className="px-4 py-2 font-semibold">{item.code}</td>
                    <td className="px-4 py-2">{item.name}</td>
                    <td className="px-4 py-2">{item.category?.name || "-"}</td>
                    <td className="px-4 py-2 text-xs text-muted">
                      {locs.length === 0
                        ? "-"
                        : locs.map((ls) => (
                            <div key={ls.location_id}>
                              {ls.location_name}: {ls.damaged}
                            </div>
                          ))}
                    </td>
                    <td className="px-4 py-2 text-center">
                      {item.quantity_available}
                    </td>
                    <td className="px-4 py-2 text-center text-red-600 font-semibold">
                      {item.quantity_damaged}
                    </td>
                    <td className="px-4 py-2 text-right">
                      <button
                        type="button"
                        disabled={locs.length === 0}
                        onClick={() => openRepair(item)}
                        className="rounded-md bg-green-600 text-white text-xs font-semibold px-3 py-1.5 hover:bg-green-700 disabled:opacity-40"
                        title="Tandai sudah diperbaiki — kembali jadi stok tersedia"
                      >
                        Perbaiki
                      </button>
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 mt-3">
        <div className="flex items-center gap-2">
          <button
            className="rounded-md border border-white/50 dark:border-white/15 px-3 py-1.5 text-xs font-medium disabled:opacity-30"
            disabled={page === 1}
            onClick={() => setPage(1)}
          >
            First
          </button>

          <button
            className="rounded-md border border-white/50 dark:border-white/15 px-3 py-1.5 text-xs font-medium disabled:opacity-30"
            disabled={page <= 1}
            onClick={() => setPage(page - 1)}
          >
            Prev
          </button>
        </div>

        <div className="text-xs text-muted">
          Page {page} of {totalPages}
        </div>

        <div className="flex items-center gap-2">
          <button
            className="rounded-md border border-white/50 dark:border-white/15 px-3 py-1.5 text-xs font-medium disabled:opacity-30"
            disabled={page >= totalPages}
            onClick={() => setPage(page + 1)}
          >
            Next
          </button>

          <button
            className="rounded-md border border-white/50 dark:border-white/15 px-3 py-1.5 text-xs font-medium disabled:opacity-30"
            disabled={page === totalPages}
            onClick={() => setPage(totalPages)}
          >
            Last
          </button>
        </div>
      </div>

      <h2 className="text-lg font-semibold mt-8 mb-3">Riwayat Perbaikan</h2>
      <div className="glass-table-wrap overflow-x-auto">
        <table className="glass-table">
          <thead>
            <tr>
              <th className="px-4 py-2 text-left">Tanggal</th>
              <th className="px-4 py-2 text-left">Barang</th>
              <th className="px-4 py-2 text-left">Gudang</th>
              <th className="px-4 py-2 text-right">Jumlah</th>
              <th className="px-4 py-2 text-left">Diperbaiki oleh</th>
              <th className="px-4 py-2 text-left">Dicatat oleh</th>
              <th className="px-4 py-2 text-left">Status</th>
            </tr>
          </thead>
          <tbody>
            {repairs.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-4 text-center text-muted">
                  Belum ada barang yang diperbaiki.
                </td>
              </tr>
            )}
            {repairs.map((tx) => (
              <tr key={tx.id} className="border-t">
                <td className="px-4 py-2">{formatDateTime(tx.created_at)}</td>
                <td className="px-4 py-2">
                  <div className="font-semibold">{tx.item?.code || "-"}</div>
                  <div className="text-xs text-muted">{tx.item?.name || ""}</div>
                </td>
                <td className="px-4 py-2 text-muted">{tx.location_name || "-"}</td>
                <td className="px-4 py-2 text-right font-semibold">{tx.quantity}</td>
                <td className="px-4 py-2">
                  {tx.assign_username || tx.assign_name || "-"}
                  {tx.note && <div className="text-xs text-muted">{tx.note}</div>}
                </td>
                <td className="px-4 py-2 text-muted">{tx.user || "-"}</td>
                <td className="px-4 py-2 text-muted">
                  {tx.status === "PENDING"
                    ? "Menunggu acc"
                    : tx.status === "REJECTED"
                    ? "Ditolak"
                    : "Selesai"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {repairItem && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4 animate-fade-in">
          <div className="glass-card w-full max-w-md max-h-[90vh] overflow-y-auto p-6">
            <h2 className="text-lg font-semibold">Perbaiki Barang</h2>
            <p className="text-sm text-muted mb-4">
              {repairItem.code} — {repairItem.name}. Yang sudah diperbaiki kembali jadi stok
              tersedia di gudang yang sama.
            </p>

            <form onSubmit={submitRepair} className="space-y-3">
              <div>
                <label className="block text-xs font-medium mb-1">Gudang</label>
                <select
                  value={repairLocationId}
                  onChange={(e) => {
                    setRepairLocationId(e.target.value);
                    setRepairQty("");
                  }}
                  className={inputClass}
                >
                  <option value="">Pilih gudang…</option>
                  {damagedLocations(repairItem).map((ls) => (
                    <option key={ls.location_id} value={ls.location_id}>
                      {ls.location_name} (rusak: {ls.damaged})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium mb-1">
                  Jumlah diperbaiki
                  {maxRepairQty > 0 && (
                    <span className="text-muted font-normal"> — maksimal {maxRepairQty}</span>
                  )}
                </label>
                <input
                  type="number"
                  min="1"
                  max={maxRepairQty || undefined}
                  value={repairQty}
                  onChange={(e) => setRepairQty(e.target.value)}
                  className={inputClass}
                />
              </div>

              <div>
                <label className="block text-xs font-medium mb-1">Diperbaiki oleh</label>
                <input
                  type="text"
                  value={repairBy}
                  onChange={(e) => setRepairBy(e.target.value)}
                  placeholder="mis. Pak Joko / Tim Teknisi / vendor"
                  className={inputClass}
                />
              </div>

              <div>
                <label className="block text-xs font-medium mb-1">
                  Catatan <span className="text-muted font-normal">(opsional)</span>
                </label>
                <textarea
                  rows={2}
                  value={repairNote}
                  onChange={(e) => setRepairNote(e.target.value)}
                  placeholder="mis. ganti konektor, solder ulang"
                  className={inputClass}
                />
              </div>

              {repairError && <p className="text-red-600 text-xs">{repairError}</p>}

              <div className="flex gap-2 justify-end pt-2">
                <button
                  type="button"
                  onClick={closeRepair}
                  className="px-4 py-2 rounded-lg border border-white/50 dark:border-white/15 text-sm"
                >
                  Batal
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 rounded-lg bg-green-600 text-white text-sm font-semibold hover:bg-green-700 disabled:opacity-50"
                >
                  {saving ? "Menyimpan..." : "Simpan"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default DamagedItems;
